// Browser-side store for every narration / memory clip so playback never waits on the network.
// Cache API (persistent) when available, in-memory blobs otherwise; invalidated by the server's audio_version.
const API = process.env.REACT_APP_BACKEND_URL;
const CACHE = "spore-audio-v1";
const VERSION_KEY = "spore-audio-version";
const memory = new Map();

const openCache = async () => {
  try { return typeof caches !== "undefined" ? await caches.open(CACHE) : null; } catch { return null; }
};

export const splitSegments = (text = "") => text.split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);

export const clipUrl = (path) => `${API}/api/${path}`;

// Every clip the kiosk can play for this content snapshot.
export const clipList = (game) => {
  const n = game?.narration || {};
  const list = [];
  splitSegments(n.briefing).forEach((_, i) => list.push(clipUrl(`narration/briefing?seg=${i}`)));
  ["note_25", "note_50", "note_75", "finale"].forEach((p) => { if (n[p]) list.push(clipUrl(`narration/${p}`)); });
  (game?.specimens || []).forEach((s) => {
    if (s.memory?.script) list.push(clipUrl(`memory/${s.id}/voice`));
    list.push(clipUrl(`memory/${s.id}/sfx`));
  });
  return list;
};

const matchCached = async (url) => {
  const c = await openCache();
  if (c) return (await c.match(url)) || null;
  const blob = memory.get(url);
  return blob ? new Response(blob) : null;
};

const fetchWithTimeout = (url, ms) => {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), ms);
  return fetch(url, { credentials: "include", signal: ctrl.signal }).finally(() => clearTimeout(t));
};

// Response from the store, else from the network (and stored). null = the server has no such clip (404).
export const loadClip = async (url, { timeoutMs = 25000 } = {}) => {
  const hit = await matchCached(url);
  if (hit) return hit;
  const res = await fetchWithTimeout(url, timeoutMs);
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`audio ${res.status}`);
  const c = await openCache();
  if (c) await c.put(url, res.clone());
  else memory.set(url, await res.clone().blob());
  return res;
};

export const clipObjectUrl = async (url) => {
  const r = await loadClip(url);
  return r ? URL.createObjectURL(await r.blob()) : null;
};

export const clipBuffer = async (url) => {
  const r = await loadClip(url);
  return r ? r.arrayBuffer() : null;
};

// Drops everything when the server's content changed since the last boot.
export const ensureVersion = async (version) => {
  if (!version || localStorage.getItem(VERSION_KEY) === version) return false;
  try { if (typeof caches !== "undefined") await caches.delete(CACHE); } catch { /* nothing to drop */ }
  memory.clear();
  localStorage.setItem(VERSION_KEY, version);
  return true;
};

// Sequential download (kind to a weak link); cached clips resolve instantly.
export const preloadClips = async (urls, { onProgress, shouldStop } = {}) => {
  let done = 0;
  const failed = [];
  for (const url of urls) {
    if (shouldStop?.()) return { done, failed, stopped: true };
    try { await loadClip(url, { timeoutMs: 90000 }); done += 1; } catch { failed.push(url); }
    onProgress?.({ done, total: urls.length, failed: failed.length });
  }
  return { done, failed, stopped: false };
};
